import {isEqual, omit, sortBy} from 'lodash';
import {useEffect, useMemo, useRef, useState} from 'react';

import {CompanyKeyEnum} from '../../../../service/company-v2/company-const';
import {CompanyFormType} from '../../../../service/company-v2/company-type';
import {Form, FormInstance} from '../../../../typings/antd';

import {normalizeSocialNetworkFormItemForDirty, syncAttributesWithForm} from './company-form-helper';
import {useAttributesState} from './widgets/attributes/attributes-context';
import {isAttributeValueChanged} from './widgets/attributes/attributes-form-helper';
import {
    AttributeGroupType,
    AttributeType,
    AttributeTypeEnum,
    BooleanAttributeType,
    DoubleGisAttributeGetCompanyType,
    YandexAttributeGetCompanyType,
} from './widgets/attributes/attributes-type';

function asRecord(value: unknown): Record<string, unknown> {
    return (value ?? {}) as Record<string, unknown>;
}

function getNonEmptyStringProperty(record: unknown, key: string): string | null {
    const value = asRecord(record)[key];

    return typeof value === 'string' && value.length > 0 ? value : null;
}

type AttributesFormType = CompanyFormType[CompanyKeyEnum.Attributes];

function serializeGoogleAttributeValueFromForm(
    attributeValue: AttributeType['values'][0],
    featureType: AttributeTypeEnum
): string | boolean {
    const isBooleanType =
        featureType === AttributeTypeEnum.GoogleBoolean || featureType === AttributeTypeEnum.DoubleGisBoolean;

    if (isBooleanType && 'selected' in attributeValue && attributeValue.selected) {
        return Boolean(Number(attributeValue.templateId));
    }

    if (featureType === AttributeTypeEnum.GoogleUrl && 'value' in attributeValue && attributeValue.value) {
        return attributeValue.value;
    }

    return attributeValue.templateId.toString();
}

function extractGoogleFingerprintEntries(attributes: AttributesFormType) {
    return (attributes.attributesGoogle ?? [])
        .flatMap((entry) => ('items' in entry ? (entry as AttributeGroupType).items : []))
        .map((attributeItem) => {
            const changedValues = [...attributeItem.values].filter((attributeValue: AttributeType['values'][number]) =>
                isAttributeValueChanged(asRecord(attributeValue))
            );

            if (changedValues.length === 0) {
                return null;
            }

            return {
                id: attributeItem.internalName,
                values: changedValues.map((attributeValue: AttributeType['values'][number]) =>
                    serializeGoogleAttributeValueFromForm(attributeValue, attributeItem.featureType)
                ),
            };
        })
        .filter(Boolean);
}

function extractYandexFingerprintEntries(attributes: AttributesFormType) {
    return (attributes.attributesYandex ?? []).flatMap((entry) => {
        const attribute = entry as YandexAttributeGetCompanyType | AttributeType;
        const attributeValues = attribute.values as Array<AttributeType['values'][0]>;
        const changedValues = attributeValues.filter((attributeValue: AttributeType['values'][0]) =>
            isAttributeValueChanged(asRecord(attributeValue))
        );

        return changedValues.map((attributeValue) => {
            const base = {featureType: attribute.featureType, templateId: attributeValue.templateId};
            const value = getNonEmptyStringProperty(attributeValue, 'value');
            const valueFrom = getNonEmptyStringProperty(attributeValue, 'valueFrom');
            const valueTo = getNonEmptyStringProperty(attributeValue, 'valueTo');
            const withValue = value ? {value} : {};
            const withFrom = valueFrom ? {valueFrom} : {};
            const withTo = valueTo ? {valueTo} : {};

            return {...base, ...withValue, ...withFrom, ...withTo};
        });
    });
}

function extractDoubleGisFingerprintEntries(attributes: AttributesFormType) {
    return (attributes.attributesDoubleGis ?? [])
        .flatMap((entry) => {
            if ('originId' in entry) {
                const doubleGisEntry = entry as DoubleGisAttributeGetCompanyType;
                const sortedValues = [...doubleGisEntry.values].sort();

                return sortedValues.length > 0 ? [{id: doubleGisEntry.originId, values: sortedValues}] : [];
            }

            const booleanEntry = entry as BooleanAttributeType;
            const changedValues = booleanEntry.values.filter((attributeValue) =>
                isAttributeValueChanged(asRecord(attributeValue))
            );

            if (changedValues.length === 0) {
                return [];
            }

            return [
                {
                    id: booleanEntry.internalName,
                    values: changedValues.map((attributeValue) =>
                        serializeGoogleAttributeValueFromForm(attributeValue, AttributeTypeEnum.DoubleGisBoolean)
                    ),
                },
            ];
        })
        .filter((entry) => entry.values.length > 0);
}

function fingerprintAttributes(attributes?: AttributesFormType): string {
    if (!attributes) {
        return '';
    }

    const google = extractGoogleFingerprintEntries(attributes)
        .sort((leftAttribute, rightAttribute) => leftAttribute.id.localeCompare(rightAttribute.id))
        .map((googleAttribute) => ({...googleAttribute, values: [...googleAttribute.values].sort()}));

    const yandex = extractYandexFingerprintEntries(attributes).sort((leftEntry, rightEntry) => {
        const featureTypeCompare = leftEntry.featureType.localeCompare(rightEntry.featureType);

        if (featureTypeCompare !== 0) {
            return featureTypeCompare;
        }

        const templateIdCompare = String(leftEntry.templateId).localeCompare(String(rightEntry.templateId));

        if (templateIdCompare !== 0) {
            return templateIdCompare;
        }

        return (leftEntry.value ?? '').localeCompare(rightEntry.value ?? '');
    });

    const doubleGis = extractDoubleGisFingerprintEntries(attributes)
        .sort((leftAttribute, rightAttribute) => leftAttribute.id.localeCompare(rightAttribute.id))
        .map((doubleGisAttribute) => ({...doubleGisAttribute, values: [...doubleGisAttribute.values].sort()}));

    return JSON.stringify({google, yandex, doubleGis});
}

function normalizeMappedCatalogCategoriesForDirty(
    mapped: Partial<CompanyFormType>['mappedCatalogCategories']
): Partial<CompanyFormType>['mappedCatalogCategories'] {
    if (!Array.isArray(mapped)) {
        return mapped;
    }

    return sortBy(
        mapped.map((catalog) => {
            if (!Array.isArray(catalog.categories)) {
                return catalog;
            }

            return {
                ...catalog,
                categories: sortBy(catalog.categories, [
                    (category) => String(category.originId ?? ''),
                    (category) => (category.isMain ? 0 : 1),
                ]),
            };
        }),
        (catalog) => catalog.catalogId
    );
}

function normalizeRootSocialNetworksForDirty(
    list: Partial<CompanyFormType>['socialNetworks']
): Partial<CompanyFormType>['socialNetworks'] {
    if (!Array.isArray(list)) {
        return list;
    }

    return sortBy(
        list.map((item) =>
            normalizeSocialNetworkFormItemForDirty({
                protocol: item.protocol,
                value: typeof item.value === 'string' ? item.value : '',
            })
        ),
        (item) => `${item.protocol}${item.value}`
    );
}

function normalizeExtraSocialNetworksForDirty(
    entries: Partial<CompanyFormType>['extraSocialNetworks']
): Partial<CompanyFormType>['extraSocialNetworks'] {
    if (!Array.isArray(entries)) {
        return entries;
    }

    return sortBy(
        entries.map((entry) => ({
            ...entry,
            socialNetworks: Array.isArray(entry.socialNetworks)
                ? sortBy(
                      entry.socialNetworks.map((item) =>
                          normalizeSocialNetworkFormItemForDirty({
                              protocol: item.protocol,
                              value: typeof item.value === 'string' ? item.value : '',
                          })
                      ),
                      (item) => `${item.protocol}${item.value}`
                  )
                : entry.socialNetworks,
        })),
        (entry) => entry.catalogId
    );
}

function normalizeForDirtyCheck(values: Partial<CompanyFormType>) {
    const normalized = omit(values, [CompanyKeyEnum.SubmitTimestamp]) as Partial<CompanyFormType>;

    if (Array.isArray(normalized.catalogCategories)) {
        normalized.catalogCategories = normalized.catalogCategories.map((catalog) => {
            const cleaned = omit(catalog as unknown as Record<string, unknown>, ['edited', 'reset']) as typeof catalog;

            if (Array.isArray(cleaned.categories)) {
                return {
                    ...cleaned,
                    categories: sortBy(cleaned.categories, [
                        (category) => category.originId,
                        (category) => (category.isMain ? 0 : 1),
                    ]),
                };
            }

            return cleaned;
        });
    }

    if (Array.isArray(normalized.mappedCatalogCategories)) {
        normalized.mappedCatalogCategories = normalizeMappedCatalogCategoriesForDirty(
            normalized.mappedCatalogCategories
        );
    }

    if (Array.isArray(normalized.socialNetworks)) {
        normalized.socialNetworks = normalizeRootSocialNetworksForDirty(normalized.socialNetworks);
    }

    if (Array.isArray(normalized.extraSocialNetworks)) {
        normalized.extraSocialNetworks = normalizeExtraSocialNetworksForDirty(normalized.extraSocialNetworks);
    }

    return normalized;
}

function getEffectiveAttributes(
    state: Map<string, AttributeType> | null,
    formAttributes?: AttributesFormType
): AttributesFormType | undefined {
    if (!formAttributes) {
        return formAttributes;
    }

    if (!state || state.size === 0) {
        return formAttributes;
    }

    return syncAttributesWithForm(state, formAttributes);
}

function isFormDifferent(
    getAttributes: () => Map<string, AttributeType>,
    baselineAttributesState: Map<string, AttributeType> | null,
    current: Partial<CompanyFormType>,
    baseline: Partial<CompanyFormType>
) {
    const normalizedCurrent = normalizeForDirtyCheck(current);
    const normalizedBaseline = normalizeForDirtyCheck(baseline);

    const nonAttributesDifferent = !isEqual(
        omit(normalizedCurrent, [CompanyKeyEnum.Attributes]),
        omit(normalizedBaseline, [CompanyKeyEnum.Attributes])
    );

    const effectiveCurrentAttributes = getEffectiveAttributes(getAttributes(), normalizedCurrent.attributes);
    const effectiveBaselineAttributes = getEffectiveAttributes(baselineAttributesState, normalizedBaseline.attributes);

    const attributesDifferent =
        fingerprintAttributes(effectiveCurrentAttributes) !== fingerprintAttributes(effectiveBaselineAttributes);

    return nonAttributesDifferent || attributesDifferent;
}

export function useCompanyFormDirty(options: {
    form: FormInstance<CompanyFormType>;
    initialValues: Partial<CompanyFormType>;
    isEdit: boolean;
}): {isDirty: boolean; resetBaseline: () => void} {
    const {form, initialValues, isEdit} = options;
    const {getAttributes, attributesState} = useAttributesState();

    const watchedAllValues = Form.useWatch([], form);
    const watchedAttributes = Form.useWatch(CompanyKeyEnum.Attributes, form);

    const comparableInitialValues = useMemo(
        () => omit(initialValues, [CompanyKeyEnum.SubmitTimestamp]),
        [initialValues]
    );

    const [isDirty, setIsDirty] = useState(false);
    const baselineFormValuesRef = useRef<Partial<CompanyFormType> | null>(null);
    const baselineAttributesStateRef = useRef<typeof attributesState | null>(null);

    function recalc(): void {
        if (!isEdit) {
            return;
        }

        const baseline = baselineFormValuesRef.current ?? comparableInitialValues;

        setIsDirty(
            isFormDifferent(getAttributes, baselineAttributesStateRef.current, form.getFieldsValue(true), baseline)
        );
    }

    useEffect(() => {
        if (!isEdit) {
            return;
        }

        const timeoutId = setTimeout(() => {
            const currentComparable = normalizeForDirtyCheck(form.getFieldsValue(true));

            baselineFormValuesRef.current = currentComparable;
            baselineAttributesStateRef.current = attributesState;
            setIsDirty(false);
        }, 100);

        // eslint-disable-next-line consistent-return
        return () => clearTimeout(timeoutId);
    }, [comparableInitialValues, form, isEdit]);

    useEffect(() => {
        recalc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedAllValues, watchedAttributes, attributesState, isEdit]);

    function resetBaseline(): void {
        baselineFormValuesRef.current = normalizeForDirtyCheck(form.getFieldsValue(true));
        baselineAttributesStateRef.current = attributesState;
        setIsDirty(false);
    }

    return {isDirty, resetBaseline};
}
